import { useEffect, useState } from "react";
import { supabase } from "../../../../lib/supabase";

interface PhoneEntry {
  id: string;
  phone_number: string;
  status: "available" | "assigned";
  assigned_to: string | null;
  assigned_at: string | null;
  notes: string | null;
  created_at: string;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

export default function PhonePoolPanel() {
  const [phones, setPhones] = useState<PhoneEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [bulkInput, setBulkInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState<{ added: number; duplicates: number } | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const availableCount = phones.filter((p) => p.status === "available").length;
  const assignedCount = phones.filter((p) => p.status === "assigned").length;

  const fetchPhones = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("phone_pool")
      .select("id, phone_number, status, assigned_to, assigned_at, notes, created_at")
      .order("created_at", { ascending: false });
    setPhones((data as PhoneEntry[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchPhones();
  }, []);

  const handleAddNumbers = async () => {
    const lines = bulkInput
      .split(/[\n,]+/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) return;

    setAdding(true);
    try {
      const rows = lines.map((phone_number) => ({ phone_number, status: "available" }));
      const { data, error } = await supabase
        .from("phone_pool")
        .upsert(rows, { onConflict: "phone_number", ignoreDuplicates: true })
        .select("id");

      const added = data?.length ?? 0;
      const duplicates = lines.length - added;
      setAddResult({ added, duplicates });
      setBulkInput("");
      setShowAddForm(false);
      fetchPhones();
      setTimeout(() => setAddResult(null), 4000);
    } finally {
      setAdding(false);
    }
  };

  const handleReleasePhone = async (id: string) => {
    if (!confirm("¿Liberar este número y ponerlo como disponible? Solo hazlo si el cliente ya no lo usa.")) return;
    await supabase
      .from("phone_pool")
      .update({ status: "available", assigned_to: null, assigned_at: null })
      .eq("id", id);
    setPhones((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status: "available", assigned_to: null, assigned_at: null } : p))
    );
  };

  const handleDeletePhone = async (id: string, phone: string) => {
    if (!confirm(`¿Eliminar el número ${phone} de la bolsa permanentemente?`)) return;
    await supabase.from("phone_pool").delete().eq("id", id);
    setPhones((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <div className="space-y-4">
      {/* Stats header */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-stone-100 rounded-2xl px-6 py-4 flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl">
            <i className="ri-phone-line text-emerald-600 text-lg" />
          </div>
          <div>
            <p className="text-2xl font-bold text-stone-900">{availableCount}</p>
            <p className="text-xs text-stone-400 font-medium">Disponibles</p>
          </div>
        </div>
        <div className="bg-white border border-stone-100 rounded-2xl px-6 py-4 flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl">
            <i className="ri-user-voice-line text-amber-600 text-lg" />
          </div>
          <div>
            <p className="text-2xl font-bold text-stone-900">{assignedCount}</p>
            <p className="text-xs text-stone-400 font-medium">Asignados</p>
          </div>
        </div>
        <div className="bg-white border border-stone-100 rounded-2xl px-6 py-4 flex items-center gap-4">
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${availableCount === 0 ? "bg-rose-50" : "bg-stone-50"}`}>
            <i className={`ri-database-2-line text-lg ${availableCount === 0 ? "text-rose-500" : "text-stone-400"}`} />
          </div>
          <div>
            <p className={`text-2xl font-bold ${availableCount === 0 ? "text-rose-600" : "text-stone-900"}`}>{phones.length}</p>
            <p className="text-xs text-stone-400 font-medium">
              {availableCount === 0 ? "Bolsa vacía — añade números" : "Total en bolsa"}
            </p>
          </div>
        </div>
      </div>

      {/* Low stock alert */}
      {availableCount <= 2 && availableCount > 0 && (
        <div className="flex items-center gap-3 px-5 py-3.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-alert-line text-amber-500" />
          </div>
          <p>
            <strong>Quedan solo {availableCount} número{availableCount !== 1 ? "s" : ""} disponible{availableCount !== 1 ? "s" : ""}</strong> — añade más antes de que se agoten para que los nuevos clientes reciban su número al instante.
          </p>
        </div>
      )}
      {availableCount === 0 && phones.length > 0 && (
        <div className="flex items-center gap-3 px-5 py-3.5 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-800">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-rose-500" />
          </div>
          <p>
            <strong>Bolsa vacía.</strong> Los próximos clientes que paguen no recibirán número automáticamente — tendrás que asignarlo manualmente.
          </p>
        </div>
      )}

      {/* Table card */}
      <div className="bg-white border border-stone-100 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-stone-900">Bolsa de números</h2>
            <p className="text-xs text-stone-400">{phones.length} números en total · asignación automática al pagar</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchPhones}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-stone-200 text-stone-500 hover:bg-stone-50 transition-colors cursor-pointer"
              title="Actualizar"
            >
              <i className="ri-refresh-line text-sm" />
            </button>
            <button
              onClick={() => setShowAddForm((v) => !v)}
              className="flex items-center gap-2 px-4 py-2 bg-stone-900 hover:bg-stone-700 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Añadir números
            </button>
          </div>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="px-6 py-5 border-b border-stone-100 bg-stone-50">
            <p className="text-sm font-semibold text-stone-700 mb-1">Añadir números a la bolsa</p>
            <p className="text-xs text-stone-400 mb-3">Escribe un número por línea o separados por comas. Formato: <span className="font-mono bg-stone-100 px-1 py-0.5 rounded">+34 612 345 678</span></p>
            <textarea
              value={bulkInput}
              onChange={(e) => setBulkInput(e.target.value)}
              placeholder={"+34 612 345 678\n+34 623 456 789\n+34 634 567 890"}
              rows={4}
              className="w-full px-3 py-2.5 text-sm font-mono bg-white border border-stone-200 rounded-xl text-stone-700 placeholder-stone-300 focus:outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-100 transition-all resize-none"
            />
            <div className="flex items-center gap-3 mt-3">
              <button
                onClick={handleAddNumbers}
                disabled={adding || !bulkInput.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-200 disabled:text-stone-400 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {adding ? (
                  <><i className="ri-loader-4-line animate-spin" /> Añadiendo...</>
                ) : (
                  <><i className="ri-check-line" /> Confirmar</>
                )}
              </button>
              <button
                onClick={() => { setShowAddForm(false); setBulkInput(""); }}
                className="px-4 py-2 text-sm text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Success message */}
        {addResult && (
          <div className="mx-6 mt-3 flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-700">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-check-double-line text-emerald-500" />
            </div>
            <p>
              <strong>{addResult.added} número{addResult.added !== 1 ? "s" : ""} añadido{addResult.added !== 1 ? "s" : ""}</strong> a la bolsa.
              {addResult.duplicates > 0 && ` ${addResult.duplicates} duplicado${addResult.duplicates !== 1 ? "s" : ""} ignorado${addResult.duplicates !== 1 ? "s" : ""}.`}
            </p>
          </div>
        )}

        {/* Phone list */}
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-stone-400">
            <i className="ri-loader-4-line animate-spin text-xl" />
            <span className="text-sm">Cargando bolsa de números...</span>
          </div>
        ) : phones.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-stone-400">
            <div className="w-14 h-14 flex items-center justify-center bg-stone-50 rounded-2xl mb-3">
              <i className="ri-phone-line text-2xl" />
            </div>
            <p className="text-sm font-medium text-stone-500">Bolsa vacía</p>
            <p className="text-xs text-stone-400 mt-1">Añade números para que se asignen automáticamente al pagar</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Añadir el primer número
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-100">
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Número</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Estado</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Asignado a</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Fecha asign.</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Añadido</th>
                  <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {phones.map((phone) => (
                  <tr key={phone.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono font-bold text-stone-900 text-sm">{phone.phone_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      {phone.status === "available" ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-lg font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                          Disponible
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs rounded-lg font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                          Asignado
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {phone.assigned_to ? (
                        <span className="text-sm text-stone-600 truncate max-w-[200px] block">{phone.assigned_to}</span>
                      ) : (
                        <span className="text-stone-300 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-500 whitespace-nowrap">
                      {formatDate(phone.assigned_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-400 whitespace-nowrap">
                      {formatDate(phone.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {phone.status === "assigned" && (
                          <button
                            onClick={() => handleReleasePhone(phone.id)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-stone-50 hover:bg-amber-50 hover:text-amber-600 border border-stone-200 rounded-lg text-xs font-semibold text-stone-500 transition-colors cursor-pointer whitespace-nowrap"
                            title="Liberar número (poner como disponible)"
                          >
                            <i className="ri-arrow-go-back-line text-xs" />
                            Liberar
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePhone(phone.id, phone.phone_number)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-50 hover:bg-rose-50 hover:text-rose-500 text-stone-400 transition-colors cursor-pointer"
                          title="Eliminar de la bolsa"
                        >
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-stone-50 border border-stone-100 rounded-2xl px-6 py-5">
        <p className="text-xs font-bold text-stone-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
          <i className="ri-information-line text-stone-400" />
          ¿Cómo funciona la asignación automática?
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: "ri-bank-card-line", title: "1. Cliente paga", desc: "Stripe confirma el pago y activa el webhook." },
            { icon: "ri-phone-find-line", title: "2. Número asignado", desc: "El sistema coge el primer número disponible y lo reserva al instante." },
            { icon: "ri-mail-check-line", title: "3. Email enviado", desc: "El cliente recibe el email de bienvenida con su número Auria ya incluido." },
          ].map((step) => (
            <div key={step.title} className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-white border border-stone-200 rounded-xl flex-shrink-0">
                <i className={`${step.icon} text-stone-600 text-sm`} />
              </div>
              <div>
                <p className="text-xs font-bold text-stone-700">{step.title}</p>
                <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
